import {useEffect, useState} from "react";
import {toast} from "sonner";
import {
    Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter,
} from "@/components/ui/sheet.tsx";
import {Button, LoadingButton} from "@/components/ui/button.tsx";
import {Input} from "@/components/ui/input.tsx";
import {Textarea} from "@/components/ui/textarea.tsx";
import {Label} from "@/components/ui/label.tsx";
import {Switch} from "@/components/ui/switch.tsx";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select.tsx";
import {ScrollArea} from "@/components/ui/scroll-area.tsx";
import {Separator} from "@/components/ui/separator.tsx";
import {AudioPickerField} from "@/pages/sclass-test/components/AudioPickerField.tsx";
import {TagListInput} from "@/pages/sclass-test/components/TagListInput.tsx";
import {getTypeFieldConfig, GEMINI_LANGUAGE_OPTIONS, GEMINI_MODEL_OPTIONS} from "@/pages/sclass-test/typeFieldConfig.ts";
import {formatApiError} from "@/pages/sclass-test/apiError.ts";
import {IVR_FIELD_TYPE_OPTIONS, IvrField, IvrFieldType} from "@/types/SClassTest.ts";
import {useCreateIvrFieldMutation, useUpdateIvrFieldMutation} from "@/store/sclassTest.ts";

type FormState = {
    desc: string;
    type: IvrFieldType;
    order: string;
    field_speach_id: number | null;
    qa_correct_sound_id: number | null;
    qa_failed_sound_id: number | null;
    qa_after_sound_id: number | null;
    score: string;
    to_confirm: boolean;
    loop: boolean;
    timeout: string;
    max_digits: string;
    min_digits: string;
    note: string;
    option_rules: string;
    correct_options: string[];
    goto_options: string;
    advanced_modules: string;
    stt_language: string;
    gemini_api_key: string;
    gemini_language: string;
    gemini_model: string;
    gemini_prompt_type: string;
    gemini_custom_prompt: string;
    gemini_num_speakers: string;
};

function buildEmptyForm(order: number): FormState {
    return {
        desc: '', type: 'DIG', order: order.toString(),
        field_speach_id: null, qa_correct_sound_id: null, qa_failed_sound_id: null, qa_after_sound_id: null,
        score: '', to_confirm: true, loop: true, timeout: '25',
        max_digits: '10', min_digits: '0', note: '', option_rules: '1234',
        correct_options: [], goto_options: '', advanced_modules: '',
        stt_language: '', gemini_api_key: '', gemini_language: '', gemini_model: 'gemini-flash-latest',
        gemini_prompt_type: 'basic', gemini_custom_prompt: '', gemini_num_speakers: '',
    };
}

function ivrFieldToForm(field: IvrField): FormState {
    const settings = field.settings || {};
    return {
        desc: field.desc || '',
        type: field.type,
        order: field.order?.toString() ?? '',
        field_speach_id: field.field_speach_id,
        qa_correct_sound_id: field.qa_correct_sound_id,
        qa_failed_sound_id: field.qa_failed_sound_id,
        qa_after_sound_id: field.qa_after_sound_id,
        score: field.score?.toString() ?? '',
        to_confirm: field.to_confirm ?? true,
        loop: field.loop ?? true,
        timeout: field.timeout?.toString() ?? '',
        max_digits: field.max_digits?.toString() ?? '',
        min_digits: field.min_digits?.toString() ?? '',
        note: field.note || '',
        option_rules: field.option_rules || '',
        correct_options: field.correct_options || [],
        goto_options: (field.goto_options || []).join('\n'),
        advanced_modules: field.advanced_modules || '',
        stt_language: settings.stt_language || '',
        gemini_api_key: settings.gemini_api_key || '',
        gemini_language: settings.gemini_language || '',
        gemini_model: settings.gemini_model || 'gemini-flash-latest',
        gemini_prompt_type: settings.gemini_prompt_type || 'basic',
        gemini_custom_prompt: settings.gemini_custom_prompt || '',
        gemini_num_speakers: settings.gemini_num_speakers?.toString() ?? '',
    };
}

function toIntOrNull(v: string): number | null {
    if (v === '' || v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
}

function toLinesOrNull(v: string): string[] | null {
    const lines = v.split('\n').map(l => l.trim()).filter(Boolean);
    return lines.length ? lines : null;
}

type QuestionFormDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    sclassTestId: number;
    question?: IvrField | null;
    nextOrder?: number;
};

export function QuestionFormDialog({open, onOpenChange, sclassTestId, question, nextOrder}: QuestionFormDialogProps) {
    const isEdit = !!question;
    const [form, setForm] = useState<FormState>(() => buildEmptyForm(nextOrder ?? 1));
    const [createQuestion, {isLoading: isCreating}] = useCreateIvrFieldMutation();
    const [updateQuestion, {isLoading: isUpdating}] = useUpdateIvrFieldMutation();
    const isSaving = isCreating || isUpdating;

    useEffect(() => {
        if (open) {
            setForm(question ? ivrFieldToForm(question) : buildEmptyForm(nextOrder ?? 1));
        }
    }, [open, question, nextOrder]);

    const patch = (p: Partial<FormState>) => setForm(prev => ({...prev, ...p}));

    const config = getTypeFieldConfig(form.type);

    async function handleSave() {
        if (!form.desc.trim()) {
            toast.error('נא להזין שם לשאלה');
            return;
        }
        if (form.order.trim() === '') {
            toast.error('נא להזין מספר שאלה');
            return;
        }

        const settings: Record<string, any> = {};
        if (config.sttLanguage) {
            settings.stt_language = form.stt_language;
        }
        if (config.geminiSettings) {
            settings.gemini_api_key = form.gemini_api_key;
            settings.gemini_language = form.gemini_language;
            settings.gemini_model = form.gemini_model;
            settings.gemini_prompt_type = form.gemini_prompt_type;
            settings.gemini_custom_prompt = form.gemini_custom_prompt;
            settings.gemini_num_speakers = form.gemini_num_speakers ? Number(form.gemini_num_speakers) : null;
        }

        const payload = {
            sclass_test: sclassTestId,
            desc: form.desc.trim(),
            type: form.type,
            order: toIntOrNull(form.order) ?? (question?.order ?? nextOrder ?? 1),
            field_speach_id: form.field_speach_id,
            qa_correct_sound_id: config.correctFailedSounds ? form.qa_correct_sound_id : null,
            qa_failed_sound_id: config.correctFailedSounds ? form.qa_failed_sound_id : null,
            qa_after_sound_id: config.afterSound ? form.qa_after_sound_id : null,
            correct_answer: null,
            score: config.score ? toIntOrNull(form.score) : null,
            to_confirm: config.toConfirm ? form.to_confirm : false,
            loop: config.loop ? form.loop : false,
            timeout: config.timeout ? toIntOrNull(form.timeout) : null,
            max_digits: config.maxMinDigits ? toIntOrNull(form.max_digits) : null,
            min_digits: config.maxMinDigits ? toIntOrNull(form.min_digits) : null,
            note: form.note,
            option_rules: config.optionRules ? form.option_rules : '',
            correct_options: config.correctOptions ? (form.correct_options.length ? form.correct_options : null) : null,
            goto_options: config.gotoOptions ? toLinesOrNull(form.goto_options) : null,
            advanced_modules: form.advanced_modules,
            settings,
        };

        try {
            if (isEdit && question) {
                await updateQuestion({id: question.id, ...payload}).unwrap();
                toast.success('השאלה עודכנה בהצלחה');
            } else {
                await createQuestion(payload).unwrap();
                toast.success('השאלה נוצרה בהצלחה');
            }
            onOpenChange(false);
        } catch (e: any) {
            toast.error('שגיאה בשמירת השאלה', {description: formatApiError(e)});
        }
    }

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="left"
                className="w-full sm:max-w-xl h-full flex flex-col gap-0 p-0 overflow-hidden"
                overlayClassName="bg-transparent"
                dir="rtl"
            >
                <SheetHeader className="px-6 pt-6 pb-4 border-b border-neutral-200 shrink-0">
                    <SheetTitle>{isEdit ? 'עריכת שאלה' : 'הוספת שאלה חדשה'}</SheetTitle>
                </SheetHeader>

                <ScrollArea className="flex-1 min-h-0 px-6 py-4" dir="rtl">
                    <div className="space-y-5 pb-6">
                        <div className="grid grid-cols-3 gap-4">
                            <div className="space-y-1 col-span-2">
                                <Label>שם השאלה *</Label>
                                <Input
                                    value={form.desc}
                                    onChange={(e) => patch({desc: e.target.value})}
                                    placeholder="לדוגמה: question1"
                                />
                            </div>
                            <div className="space-y-1">
                                <Label>מספר שאלה *</Label>
                                <Input
                                    type="number"
                                    value={form.order}
                                    onChange={(e) => patch({order: e.target.value})}
                                />
                            </div>
                        </div>

                        <div className="space-y-1">
                            <Label>סוג שאלה *</Label>
                            <Select value={form.type} onValueChange={(v) => patch({type: v as IvrFieldType})}>
                                <SelectTrigger className="w-full">
                                    <SelectValue/>
                                </SelectTrigger>
                                <SelectContent>
                                    {IVR_FIELD_TYPE_OPTIONS.map(opt => (
                                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <AudioPickerField
                            label="הקלטת השאלה"
                            value={form.field_speach_id}
                            onChange={(id) => patch({field_speach_id: id})}
                        />

                        <Separator/>

                        {(config.optionRules || config.maxMinDigits || config.correctOptions) && (
                            <div className="space-y-4">
                                <h3 className="text-sm font-semibold text-neutral-700">אפשרויות תשובה</h3>

                                <div className="grid grid-cols-2 gap-4">
                                    {config.optionRules && (
                                        <div className="space-y-1 col-span-2">
                                            <Label>{config.optionRulesLabel}</Label>
                                            <Input value={form.option_rules}
                                                   onChange={(e) => patch({option_rules: e.target.value})}/>
                                        </div>
                                    )}

                                    {config.maxMinDigits && (
                                        <>
                                            <div className="space-y-1">
                                                <Label>מקסימום ספרות</Label>
                                                <Input type="number" value={form.max_digits}
                                                       onChange={(e) => patch({max_digits: e.target.value})}/>
                                            </div>
                                            <div className="space-y-1">
                                                <Label>מינימום ספרות</Label>
                                                <Input type="number" value={form.min_digits}
                                                       onChange={(e) => patch({min_digits: e.target.value})}/>
                                            </div>
                                        </>
                                    )}

                                    {config.score && (
                                        <div className="space-y-1 col-span-2">
                                            <Label>ניקוד עבור תשובה נכונה</Label>
                                            <Input type="number" value={form.score}
                                                   onChange={(e) => patch({score: e.target.value})}/>
                                        </div>
                                    )}
                                </div>

                                {config.correctOptions && (
                                    <div className="space-y-1">
                                        <Label>{config.correctOptionsLabel}</Label>
                                        <TagListInput
                                            value={form.correct_options}
                                            onChange={(v) => patch({correct_options: v})}
                                            placeholder={config.correctOptionsPlaceholder}
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        {config.sttLanguage && (
                            <div className="space-y-4">
                                <h3 className="text-sm font-semibold text-neutral-700">הגדרות זיהוי דיבור</h3>
                                <div className="space-y-1">
                                    <Label>שפת זיהוי דיבור</Label>
                                    <Select value={form.stt_language || 'none'}
                                            onValueChange={(v) => patch({stt_language: v === 'none' ? '' : v})}>
                                        <SelectTrigger className="w-full">
                                            <SelectValue/>
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">ללא הגדרת שפה (ברירת מחדל)</SelectItem>
                                            <SelectItem value="he-IL">עברית</SelectItem>
                                            <SelectItem value="en-US">אנגלית</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        )}

                        {config.geminiSettings && (
                            <div className="space-y-4">
                                <h3 className="text-sm font-semibold text-neutral-700">הגדרות Gemini AI</h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-1 col-span-2">
                                        <Label>מפתח API של Gemini (אופציונלי)</Label>
                                        <Input value={form.gemini_api_key}
                                               onChange={(e) => patch({gemini_api_key: e.target.value})}
                                               placeholder="ריק = שימוש במפתח ברירת המחדל של המערכת"/>
                                    </div>
                                    <div className="space-y-1">
                                        <Label>שפה</Label>
                                        <Select value={form.gemini_language || 'none'}
                                                onValueChange={(v) => patch({gemini_language: v === 'none' ? '' : v})}>
                                            <SelectTrigger className="w-full">
                                                <SelectValue/>
                                            </SelectTrigger>
                                            <SelectContent>
                                                {GEMINI_LANGUAGE_OPTIONS.map(opt => (
                                                    <SelectItem key={opt.value || 'none'} value={opt.value || 'none'}>
                                                        {opt.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-1">
                                        <Label>מודל</Label>
                                        <Select value={form.gemini_model}
                                                onValueChange={(v) => patch({gemini_model: v})}>
                                            <SelectTrigger className="w-full">
                                                <SelectValue/>
                                            </SelectTrigger>
                                            <SelectContent>
                                                {GEMINI_MODEL_OPTIONS.map(opt => (
                                                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-1">
                                        <Label>סוג prompt</Label>
                                        <Select value={form.gemini_prompt_type}
                                                onValueChange={(v) => patch({gemini_prompt_type: v})}>
                                            <SelectTrigger className="w-full">
                                                <SelectValue/>
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="basic">בסיסי</SelectItem>
                                                <SelectItem value="custom">מותאם אישית</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-1">
                                        <Label>מספר דוברים (אופציונלי)</Label>
                                        <Input type="number" value={form.gemini_num_speakers}
                                               onChange={(e) => patch({gemini_num_speakers: e.target.value})}/>
                                    </div>
                                    {form.gemini_prompt_type === 'custom' && (
                                        <div className="space-y-1 col-span-2">
                                            <Label>Prompt מותאם אישית</Label>
                                            <Textarea autoSize rows={3} value={form.gemini_custom_prompt}
                                                      onChange={(e) => patch({gemini_custom_prompt: e.target.value})}/>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <Separator/>

                        <div className="space-y-4">
                            <h3 className="text-sm font-semibold text-neutral-700">התנהגות והשמעה</h3>
                            <div className="grid grid-cols-2 gap-4">
                                {config.toConfirm && (
                                    <div className="flex items-center justify-between rounded-lg border border-neutral-200 px-3 py-2">
                                        <Label className="cursor-pointer">לדרוש אישור תשובה</Label>
                                        <Switch checked={form.to_confirm}
                                                onCheckedChange={(v) => patch({to_confirm: v})}/>
                                    </div>
                                )}
                                {config.loop && (
                                    <div className="flex items-center justify-between rounded-lg border border-neutral-200 px-3 py-2">
                                        <Label className="cursor-pointer">חזרה על השאלה עד לתשובה</Label>
                                        <Switch checked={form.loop} onCheckedChange={(v) => patch({loop: v})}/>
                                    </div>
                                )}
                                {config.timeout && (
                                    <div className="space-y-1">
                                        <Label>{config.timeoutLabel}</Label>
                                        <Input type="number" value={form.timeout}
                                               onChange={(e) => patch({timeout: e.target.value})}/>
                                    </div>
                                )}
                            </div>
                        </div>

                        {config.correctFailedSounds && (
                            <div className="space-y-4">
                                <h3 className="text-sm font-semibold text-neutral-700">הקלטות חיווי (אופציונלי)</h3>
                                <div className="grid grid-cols-1 gap-4">
                                    <AudioPickerField label="שמע לאחר תשובה נכונה" value={form.qa_correct_sound_id}
                                                      onChange={(id) => patch({qa_correct_sound_id: id})}/>
                                    <AudioPickerField label="שמע לאחר תשובה שגויה" value={form.qa_failed_sound_id}
                                                      onChange={(id) => patch({qa_failed_sound_id: id})}/>
                                </div>
                            </div>
                        )}

                        {config.afterSound && (
                            <AudioPickerField label="שמע להשמעה לאחר מענה (תמיד)" value={form.qa_after_sound_id}
                                              onChange={(id) => patch({qa_after_sound_id: id})}/>
                        )}

                        {config.gotoOptions && (
                            <div className="space-y-1">
                                <Label>מעבר לשאלה אחרת בהתאם לתשובה</Label>
                                <Textarea
                                    autoSize rows={2}
                                    placeholder={'שורה לכל אפשרות, לדוגמה:\n1:8\n4:ניתוק'}
                                    value={form.goto_options}
                                    onChange={(e) => patch({goto_options: e.target.value})}
                                />
                            </div>
                        )}

                        <div className="space-y-1">
                            <Label>מודולים מתקדמים</Label>
                            <Input value={form.advanced_modules}
                                   onChange={(e) => patch({advanced_modules: e.target.value})}/>
                            {config.advancedModulesHint && (
                                <p className="text-xs text-neutral-400">{config.advancedModulesHint}</p>
                            )}
                        </div>

                        <div className="space-y-1">
                            <Label>הערות פנימיות</Label>
                            <Textarea autoSize rows={2} value={form.note}
                                      onChange={(e) => patch({note: e.target.value})}/>
                        </div>
                    </div>
                </ScrollArea>

                <SheetFooter className="px-6 py-4 border-t border-neutral-200 flex-row justify-end gap-2 shrink-0 bg-white">
                    <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                        ביטול
                    </Button>
                    <LoadingButton type="button" isLoading={isSaving} onClick={handleSave}>
                        {isEdit ? 'שמירת שינויים' : 'הוספת שאלה'}
                    </LoadingButton>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
}

